import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useSensor,
  useSensors,
  PointerSensor,
  useDroppable,
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import {
  ArrowLeft,
  GripVertical,
  Layers,
  FolderTree,
  Search,
  Plus,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { ruleCompositionService } from '../../../../services/ruleCompositionService';
import {
  RuleForCompositionDto,
  RuleGroupForCompositionDto,
} from '../../../../types/ruleComposition';
import RuleGroupDetailModal from './RuleGroupDetailModal';

interface RuleCompositionProps {
  onBack: () => void;
}

// Rule type names mapping
const RULE_TYPE_NAMES: Record<number, string> = {
  1: 'Plukkregler',
  2: 'Sorteringsregler',
  3: 'Filtreringsregler',
  4: 'Grupperingsregler',
  5: 'Prioriteringsregler',
};

// Draggable Rule Card Component
function DraggableRuleCard({ rule }: { rule: RuleForCompositionDto }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `rule-${rule.ruleID}`,
    data: { type: 'rule', rule },
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: isDragging ? 1000 : 1,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center gap-2 p-3 bg-white border rounded-lg shadow-sm
        cursor-grab active:cursor-grabbing transition-all
        ${isDragging ? 'opacity-50 shadow-lg ring-2 ring-blue-400' : 'hover:shadow-md hover:border-blue-300'}
        ${!rule.activeYesNo ? 'opacity-60 bg-gray-50' : ''}
      `}
      {...listeners}
      {...attributes}
    >
      <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-gray-900 truncate">
            {rule.lng1}
          </span>
          {!rule.activeYesNo && (
            <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
              Inaktiv
            </span>
          )}
        </div>
        {rule.ruleTypeName && (
          <span className="text-xs text-gray-500">{rule.ruleTypeName}</span>
        )}
      </div>
      <div className="flex-shrink-0">
        <Layers className="w-4 h-4 text-blue-500" />
      </div>
    </div>
  );
}

// Droppable Rule Group Card Component
function DroppableRuleGroupCard({
  group,
  isOver,
  onClick,
}: {
  group: RuleGroupForCompositionDto;
  isOver: boolean;
  onClick: () => void;
}) {
  const { setNodeRef } = useDroppable({
    id: `group-${group.ruleGroupID}`,
    data: { type: 'group', group },
  });

  return (
    <div
      ref={setNodeRef}
      onClick={onClick}
      className={`
        p-4 border-2 rounded-xl transition-all cursor-pointer
        ${isOver
          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-300 scale-[1.02]'
          : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'}
        ${!group.activeYesNo ? 'opacity-60 bg-gray-50' : ''}
      `}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`
            p-2 rounded-lg
            ${isOver ? 'bg-blue-200' : 'bg-blue-100'}
          `}>
            <FolderTree className={`w-5 h-5 ${isOver ? 'text-blue-700' : 'text-blue-600'}`} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{group.lng1}</h3>
            {group.lng2 && (
              <p className="text-sm text-gray-500">{group.lng2}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!group.activeYesNo && (
            <span className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded-full">
              Inaktiv
            </span>
          )}
          <div className={`
            px-3 py-1 rounded-full text-sm font-medium
            ${group.ruleCount > 0
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-500'}
          `}>
            {group.ruleCount} {group.ruleCount === 1 ? 'regel' : 'regler'}
          </div>
        </div>
      </div>
      {isOver && (
        <div className="mt-3 pt-3 border-t border-blue-200 text-center">
          <span className="text-sm text-blue-600 font-medium flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" />
            Slipp for å legge til regel
          </span>
        </div>
      )}
    </div>
  );
}

// Drag Overlay Component (shown while dragging)
function RuleDragOverlay({ rule }: { rule: RuleForCompositionDto }) {
  return (
    <div className="flex items-center gap-2 p-3 bg-white border-2 border-blue-400 rounded-lg shadow-xl cursor-grabbing">
      <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-medium text-sm text-gray-900">{rule.lng1}</span>
      </div>
      <Layers className="w-4 h-4 text-blue-500 flex-shrink-0" />
    </div>
  );
}

export default function RuleComposition({ onBack }: RuleCompositionProps) {
  const [rules, setRules] = useState<RuleForCompositionDto[]>([]);
  const [groups, setGroups] = useState<RuleGroupForCompositionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRuleType, setSelectedRuleType] = useState<number | null>(null);
  const [activeRule, setActiveRule] = useState<RuleForCompositionDto | null>(null);
  const [overGroupId, setOverGroupId] = useState<number | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<RuleGroupForCompositionDto | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [rulesData, groupsData] = await Promise.all([
        ruleCompositionService.getAllRules(),
        ruleCompositionService.getAllGroups(),
      ]);
      setRules(rulesData);
      setGroups(groupsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feil ved lasting av data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const rule = active.data.current?.rule as RuleForCompositionDto;
    if (rule) {
      setActiveRule(rule);
    }
  };

  const handleDragOver = (event: any) => {
    const { over } = event;
    if (over && over.id.toString().startsWith('group-')) {
      const groupId = parseInt(over.id.toString().replace('group-', ''));
      setOverGroupId(groupId);
    } else {
      setOverGroupId(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveRule(null);
    setOverGroupId(null);

    if (!over) return;

    const ruleData = active.data.current?.rule as RuleForCompositionDto;
    const overIdStr = over.id.toString();

    if (ruleData && overIdStr.startsWith('group-')) {
      const groupId = parseInt(overIdStr.replace('group-', ''));

      try {
        // Check if already linked
        const isLinked = await ruleCompositionService.checkLink(ruleData.ruleID, groupId);
        if (isLinked) {
          showNotification('error', 'Regelen er allerede koblet til denne gruppen');
          return;
        }

        // Create the link
        await ruleCompositionService.createLink({
          ruleID: ruleData.ruleID,
          ruleGroupID: groupId,
        });

        // Refresh groups to update rule count
        const updatedGroups = await ruleCompositionService.getAllGroups();
        setGroups(updatedGroups);

        const group = groups.find(g => g.ruleGroupID === groupId);
        showNotification(
          'success',
          `Regel "${ruleData.lng1}" lagt til i "${group?.lng1 || 'gruppe'}"`
        );
      } catch (err) {
        showNotification(
          'error',
          err instanceof Error ? err.message : 'Feil ved kobling av regel'
        );
      }
    }
  };

  const handleDragCancel = () => {
    setActiveRule(null);
    setOverGroupId(null);
  };

  const handleGroupClick = (group: RuleGroupForCompositionDto) => {
    setSelectedGroup(group);
  };

  const handleModalClose = () => {
    setSelectedGroup(null);
  };

  const handleRulesChanged = async () => {
    // Refresh groups to update rule counts
    const updatedGroups = await ruleCompositionService.getAllGroups();
    setGroups(updatedGroups);
  };

  // Get unique rule types for filtering
  const ruleTypes = Array.from(new Set(rules.map(r => r.ruleType))).sort();

  // Filter rules based on search and type
  const filteredRules = rules.filter(rule => {
    const matchesSearch =
      searchTerm === '' ||
      rule.lng1.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (rule.lng2 && rule.lng2.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = selectedRuleType === null || rule.ruleType === selectedRuleType;
    return matchesSearch && matchesType;
  });

  // Group rules by type for display
  const rulesByType = filteredRules.reduce((acc, rule) => {
    const typeName = rule.ruleTypeName || RULE_TYPE_NAMES[rule.ruleType] || `Type ${rule.ruleType}`;
    if (!acc[typeName]) {
      acc[typeName] = [];
    }
    acc[typeName].push(rule);
    return acc;
  }, {} as Record<string, RuleForCompositionDto[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <AlertCircle className="w-12 h-12 text-red-500" />
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Prøv igjen
        </button>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="h-full flex flex-col bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Regelsammensetning
                </h1>
                <p className="text-sm text-gray-500">
                  Dra regler fra venstre og slipp dem på regelgrupper til høyre
                </p>
              </div>
            </div>
            <button
              onClick={loadData}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Oppdater"
            >
              <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div
            className={`mx-6 mt-4 p-3 rounded-lg flex items-center gap-2 ${
              notification.type === 'success'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span>{notification.message}</span>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden p-6 gap-6">
          {/* Left Panel - Rules */}
          <div className="w-1/2 flex flex-col bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Layers className="w-5 h-5 text-blue-600" />
                Tilgjengelige regler
              </h2>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Søk etter regler..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <select
                  value={selectedRuleType ?? ''}
                  onChange={(e) =>
                    setSelectedRuleType(e.target.value ? parseInt(e.target.value) : null)
                  }
                  className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Alle typer</option>
                  {ruleTypes.map((type) => (
                    <option key={type} value={type}>
                      {RULE_TYPE_NAMES[type] || `Type ${type}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {Object.keys(rulesByType).length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  Ingen regler funnet
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(rulesByType).map(([typeName, typeRules]) => (
                    <div key={typeName}>
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        {typeName} ({typeRules.length})
                      </h3>
                      <div className="space-y-2">
                        {typeRules.map((rule) => (
                          <DraggableRuleCard key={rule.ruleID} rule={rule} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Rule Groups */}
          <div className="w-1/2 flex flex-col bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <FolderTree className="w-5 h-5 text-green-600" />
                Regelgrupper
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Klikk på en gruppe for å se og redigere reglene
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {groups.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  Ingen regelgrupper funnet
                </div>
              ) : (
                <div className="space-y-3">
                  {groups.map((group) => (
                    <DroppableRuleGroupCard
                      key={group.ruleGroupID}
                      group={group}
                      isOver={overGroupId === group.ruleGroupID}
                      onClick={() => handleGroupClick(group)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeRule ? <RuleDragOverlay rule={activeRule} /> : null}
        </DragOverlay>

        {/* Rule Group Detail Modal */}
        {selectedGroup && (
          <RuleGroupDetailModal
            group={selectedGroup}
            onClose={handleModalClose}
            onRulesChanged={handleRulesChanged}
          />
        )}
      </div>
    </DndContext>
  );
}
